import type { ReactElement } from "react"

import { useTranslation } from "react-i18next"

import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined"
import { CircularProgress, Grid } from "@mui/material"
import { Button, Typography } from "@synapse-analytics/synapse-ui"

import styles from "./KonanEmptyState.module.scss"

type Props = {
  title: string
  subTitle?: string
  buttonText?: string
  setAction?: (value: boolean) => void
  actionButtons?: Array<ReactElement>
  isLoading?: boolean
}

/**
 * Basic empty state component
 * @param {string[]} title empty state title
 * @param {string} subTitle empty state subtitle
 * @param {string} buttonText text that appears on the button
 * @param {function} setAction fires up action needed by the page
 * @return {React.ReactElement}
 */
function KonanEmptyState(props: Readonly<Props>): React.ReactElement {
  const { title, subTitle, buttonText, setAction, actionButtons, isLoading } = props

  return (
    <Grid className={styles.emptyState} container>
      <Grid className={styles.emptyStateContainer} container>
        {isLoading && (
          <Grid>
            <CircularProgress />
          </Grid>
        )}
        <Grid>
          <Typography variant="h2-bold" className={styles.emptyStateTitle} align="center">
            {title}
          </Typography>
        </Grid>

        {subTitle && (
          <Grid container direction="row" justifyContent="center">
            <Typography variant="p" className={styles.emptyStateSubtitle}>
              {subTitle.charAt(0).toUpperCase() + subTitle.slice(1).toLowerCase()}
            </Typography>
          </Grid>
        )}

        {/** TODO: change component structure later to only accept actions as an array of ReactElements
         * for now .. i'm keeping both options to keep backward compatibility
         * and don't break older implementation
         */}
        {actionButtons && (
          <Grid display="flex" mt={1}>
            {actionButtons?.map((button) => (
              <Grid item mr={1}>
                {button}
              </Grid>
            ))}
          </Grid>
        )}

        {buttonText && (
          <Grid container justifyContent={"center"} mt={1}>
            <Button
              onClick={() => setAction && setAction(true)}
              variant="primary"
              color="primary"
              size="regular"
              startIcon={<AddCircleOutlineOutlinedIcon fontSize="small" />}
            >
              {buttonText}
            </Button>
          </Grid>
        )}
      </Grid>
    </Grid>
  )
}

// search sort filer empty states props
type SSFProps = { title?: string[]; text?: string }

/**
 * Filter empty state component
 * shown when the filter yields no data
 * @param {string[]} title empty state title
 * @param {string} text empty state title
 * @return {React.ReactElement}
 */
function KonanEmptyStateFilter(props: Readonly<SSFProps>): React.ReactElement {
  const { title, text } = props

  const { t } = useTranslation("components")

  return (
    <Grid container className={styles.emptyState}>
      <Grid container className={styles.emptyStateContainer}>
        <Grid>
          {text ? (
            <Typography variant="h2-bold" className={styles.emptyStateTitle}>
              {text}
            </Typography>
          ) : (
            <Typography variant="h2-bold" align="center" className={styles.emptyStateTitle}>
              {t("KonanEmptyStateFilter.title", { ns: "utility" })}{" "}
              {title &&
                title?.length > 0 &&
                `for ${title
                  .map((item) => t(`MenuFilter.options.${item}`))
                  ?.join(", ")
                  .toLowerCase()}`}
            </Typography>
          )}
        </Grid>

        <Grid container direction="row" justifyContent="center">
          <Typography variant="p" className={styles.emptyStateSubtitle}>
            {t("KonanEmptyStateFilter.description", { ns: "utility" })}
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  )
}

/**
 * search empty state component
 * shown when the search yields no data
 * @param {string} title empty state title
 * @return {React.ReactElement}
 */
function KonanEmptyStateSearch(props: Readonly<{ title: string }>): React.ReactElement {
  const { title } = props

  const { t } = useTranslation(["common", "utility", "components"])

  return (
    <Grid className={styles.emptyState} container>
      <Grid className={styles.emptyStateContainer} container>
        <Grid item xs={6}>
          <Typography variant="h2-bold" className={styles.emptyStateTitle}>
            {t("KonanEmptyState.searchTitle", {
              title: title,
            })}
          </Typography>
        </Grid>

        <Grid container direction="row" justifyContent="center">
          <Typography variant="p" className={styles.emptyStateSubtitle}>
            {t("KonanEmptyState.searchSubTitle")}
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  )
}

export { KonanEmptyStateSearch, KonanEmptyStateFilter, KonanEmptyState }
